/* Phase C5 — Trade screen, AizuEX-inspired layout.

Layout (1920×800+):
  ┌─────────────── Site Header (same as Markets / landing) ─────┐
  ├ Info Bar: pair selector + price block | 24h H/L/Vol/Amt   ┤
  ├ K-Line | Token Info tabs                                  ┤
  ├ Interval pills + chart tools | OHLC inline display        ┤
  │ Chart (TradingView/Binance) │ Order Book │ Order Form     │
  │                             │            │ + Fee row      │
  ├──────────────────────────────┼────────────┼───────────────┤
  │ Open Orders / Order History / Trade History │ Market Trad. │
  └─────────────────────────────────────────────┴──────────────┘

Brand accent: blue #022CED (per user direction). Buy=green, Sell=red.
Data: Binance live order book + ticker + tape; TradingView charts only.
*/
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ChevronDown, Star, Loader2, Search,
} from "lucide-react";
import CoinIcon from "@/components/CoinIcon";
import { Toaster, toast } from "sonner";
import Header from "@/components/landing/Header";
import PairListSidebar from "@/components/trade/PairListSidebar";
import ChartWorkspace from "@/components/trade/ChartWorkspace";
import { api, getErrorMessage, wsBaseUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// --------------------------------------------------------------------------
// Theme
// --------------------------------------------------------------------------
const T = {
  bg: "#0B1018",
  surface: "#131A24",
  surface2: "#0F151D",
  surface3: "#192231",
  border: "#1F2937",
  borderSoft: "#1A2230",
  text: "#E5E7EB",
  sub: "#94A3B8",
  dim: "#64748B",
  green: "#22C55E",
  greenSoft: "rgba(34,197,94,0.14)",
  red: "#EF4444",
  redSoft: "rgba(239,68,68,0.14)",
  blue: "#022CED",
  blueLight: "#3B82F6",
};

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------
function fmtPrice(n, quote, fixed) {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return "—";
  if (fixed != null) return v.toLocaleString("en-US", { minimumFractionDigits: fixed, maximumFractionDigits: fixed });
  if (quote === "INR") return v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  if (v < 0.01) return v.toFixed(8);
  if (v < 1) return v.toFixed(6);
  if (v < 100) return v.toFixed(4);
  return v.toLocaleString("en-US", { maximumFractionDigits: 2 });
}
function fmtQty(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return "0.00";
  if (v < 0.0001) return v.toFixed(8);
  if (v < 1) return v.toFixed(6);
  if (v < 100) return v.toFixed(4);
  return v.toLocaleString("en-US", { maximumFractionDigits: 2 });
}
function fmtPct(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
}
function timeOnly(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-US", { hour12: false });
}

// --------------------------------------------------------------------------
// Auto-reconnecting WebSocket hook
// --------------------------------------------------------------------------
function useWSChannel(url, onMessage, deps = []) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cb = useCallback(onMessage, deps);
  useEffect(() => {
    if (!url) return undefined;
    let alive = true;
    let ws;
    let timer;
    let backoff = 800;
    const connect = () => {
      try { ws = new WebSocket(url); } catch (e) { return; }
      ws.onopen = () => { backoff = 800; };
      ws.onmessage = (e) => { try { cb(JSON.parse(e.data)); } catch { /* ignore */ } };
      ws.onclose = () => {
        if (!alive) return;
        timer = setTimeout(connect, backoff);
        backoff = Math.min(Math.round(backoff * 1.7), 8000);
      };
      ws.onerror = () => { try { ws.close(); } catch (e) { /* ignore */ } };
    };
    connect();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      try { ws?.close(); } catch (e) { /* ignore */ }
    };
  }, [url, cb]);
}

// --------------------------------------------------------------------------
// Pair selector
// --------------------------------------------------------------------------
function PairSelector({ current, onSelect }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [pairs, setPairs] = useState([]);
  const [tickers, setTickers] = useState({});
  const ref = useRef(null);

  useEffect(() => {
    api.get("/api/spot/pairs").then(({ data }) => setPairs(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    let ws;
    let timer;
    let backoff = 800;
    const apply = (rows) => {
      if (!alive || !Array.isArray(rows)) return;
      setTickers((prev) => {
        const map = { ...prev };
        rows.forEach((t) => { if (t?.pair) map[t.pair] = t; });
        return map;
      });
    };
    const connect = () => {
      try { ws = new WebSocket(`${wsBaseUrl()}/api/ws/tickers`); } catch { return; }
      ws.onopen = () => { backoff = 800; };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "tickers_snapshot" || msg.type === "tickers_update") apply(msg.tickers);
          else if (msg.type === "ticker" && msg.pair) apply([msg]);
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        if (!alive) return;
        timer = setTimeout(connect, backoff);
        backoff = Math.min(Math.round(backoff * 1.7), 8000);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };
    connect();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      try { ws?.close(); } catch { /* ignore */ }
    };
  }, [open]);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return pairs;
    return pairs.filter((p) => p.pair.toLowerCase().includes(ql) || p.base.toLowerCase().includes(ql));
  }, [pairs, q]);

  return (
    <div ref={ref} className="relative" data-testid="trade-pair-selector">
      <button onClick={() => setOpen((v) => !v)} data-testid="trade-pair-toggle"
        className="flex items-center gap-1 hover:opacity-80 transition"
        style={{ color: T.sub }}>
        <span className="text-[12px] font-bold tracking-wide">{current}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute left-0 top-[26px] w-[360px] z-30 rounded-md overflow-hidden shadow-2xl"
          style={{ background: T.surface, border: `1px solid ${T.border}` }}>
          <div className="px-3 py-2.5 border-b flex items-center gap-2" style={{ borderColor: T.borderSoft }}>
            <Search size={14} style={{ color: T.sub }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pair…"
              data-testid="trade-pair-search"
              className="bg-transparent outline-none text-[13px] flex-1"
              style={{ color: T.text }} />
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-6 text-center text-[12px]" style={{ color: T.dim }}>No pairs.</div>
            )}
            {filtered.map((p) => {
              const t = tickers[p.pair];
              const ch = t?.change_pct_24h;
              const last = t?.last;
              return (
                <button key={p.pair} data-testid={`trade-pair-opt-${p.base}-${p.quote}`}
                  onClick={() => { setOpen(false); onSelect(p); }}
                  className="w-full grid grid-cols-[1fr_auto_70px] gap-3 items-center px-3 py-2 hover:bg-white/5 transition text-left">
                  <div className="flex items-center gap-2 min-w-0">
                    <CoinIcon symbol={p.base} image={p.icon} size={20} />
                    <span className="text-[13px] font-bold truncate" style={{ color: T.text }}>{p.base}</span>
                    <span className="text-[11px]" style={{ color: T.dim }}>/{p.quote}</span>
                  </div>
                  <span className="text-[12px] font-mono" style={{ color: T.text }}>
                    {last != null ? fmtPrice(last, p.quote) : "—"}
                  </span>
                  <span className="text-[12px] font-bold text-right"
                    style={{ color: ch == null ? T.dim : ch >= 0 ? T.green : T.red }}>
                    {ch == null ? "—" : fmtPct(ch)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Info bar (price block on left + 24h stats on right)
// --------------------------------------------------------------------------
function InfoBar({ pair, base, quote, ticker, onPickPair }) {
  const last = Number(ticker?.last) || 0;
  const change = Number(ticker?.change_pct_24h) || 0;
  const changeAbs = Number(ticker?.change_abs_24h) || 0;
  const high = ticker?.high_24h;
  const low = ticker?.low_24h;
  const vol = ticker?.volume_24h;
  const qvol = ticker?.quote_volume_24h;
  const up = change >= 0;

  return (
    <div className="flex items-center px-6 h-[88px] gap-10 border-b"
      style={{ background: T.surface2, borderColor: T.border }}
      data-testid="trade-info-bar">
      {/* Pair + price block */}
      <div className="flex items-center gap-4 pr-8 border-r" style={{ borderColor: T.border }}>
        <div className="flex flex-col gap-1">
          <PairSelector current={pair} onSelect={onPickPair} />
          <span className="text-[10px]" style={{ color: T.dim }}>{base}/{quote}</span>
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-[26px] font-bold font-mono"
            style={{ color: up ? T.green : T.red }}
            data-testid="info-last-price">
            {fmtPrice(last, quote)}
          </span>
          <span className="text-[12px] font-mono"
            style={{ color: up ? T.green : T.red }}
            data-testid="info-change">
            {Number.isFinite(changeAbs) ? `${changeAbs >= 0 ? "+" : ""}${changeAbs.toFixed(2)}` : "—"}
          </span>
          <span className="text-[10px]" style={{ color: T.dim }}>
            ${fmtPrice(last, "USD")}
          </span>
        </div>
      </div>
      {/* 24h stats */}
      <Stat label="24h Change" value={
        <span style={{ color: up ? T.green : T.red }}>{fmtPct(change)}</span>
      } />
      <Stat label="24h High" value={fmtPrice(high, quote)} valueColor={T.green} />
      <Stat label="24h Low" value={fmtPrice(low, quote)} valueColor={T.red} />
      <Stat label={`24h Volume (${base})`} value={fmtQty(vol)} />
      <Stat label={`24h Amount (${quote})`} value={fmtQty(qvol)} />
    </div>
  );
}

function Stat({ label, value, valueColor }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px]" style={{ color: T.dim }}>{label}</span>
      <span className="text-[13px] font-bold font-mono"
        style={{ color: valueColor || T.text }}>
        {typeof value === "string" ? value : value}
      </span>
    </div>
  );
}

// --------------------------------------------------------------------------
// Order book — view-mode icons + decimal precision selector + depth bars
// --------------------------------------------------------------------------
function OrderBookIcon({ active, mode }) {
  const c = active ? T.blueLight : T.sub;
  if (mode === "split") return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="2" y="2" width="10" height="2" fill={T.red} />
      <rect x="3" y="5" width="9" height="2" fill={T.red} opacity="0.6" />
      <rect x="2" y="8" width="10" height="2" fill={c} opacity="0.4" />
      <rect x="3" y="11" width="9" height="2" fill={T.green} />
    </svg>
  );
  if (mode === "asks") return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="2" y="3" width="10" height="2" fill={T.red} />
      <rect x="3" y="7" width="9" height="2" fill={T.red} opacity="0.7" />
      <rect x="4" y="11" width="8" height="2" fill={T.red} opacity="0.5" />
    </svg>
  );
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="4" y="1" width="8" height="2" fill={T.green} opacity="0.5" />
      <rect x="3" y="5" width="9" height="2" fill={T.green} opacity="0.7" />
      <rect x="2" y="9" width="10" height="2" fill={T.green} />
    </svg>
  );
}

function OrderBook({ snap, base, quote, onPickPrice, ticker }) {
  const [mode, setMode] = useState("split"); // split | asks | bids
  const [precision, setPrecision] = useState(8);

  const bids = useMemo(() => snap.bids || [], [snap.bids]);
  const asks = useMemo(() => snap.asks || [], [snap.asks]);
  const maxBid = useMemo(() => bids.reduce((m, l) => Math.max(m, Number(l.qty)), 0), [bids]);
  const maxAsk = useMemo(() => asks.reduce((m, l) => Math.max(m, Number(l.qty)), 0), [asks]);

  const TOP = mode === "split" ? 14 : 28;
  const askRows = asks.slice(0, TOP).slice().reverse();
  const bidRows = bids.slice(0, TOP);

  const Row = ({ lvl, side }) => {
    const qty = Number(lvl.qty);
    const max = side === "bid" ? maxBid : maxAsk;
    const w = max > 0 ? Math.min(100, (qty / max) * 100) : 0;
    const c = side === "bid" ? T.green : T.red;
    const cBg = side === "bid" ? T.greenSoft : T.redSoft;
    return (
      <button onClick={() => onPickPrice?.(lvl.price)} data-testid={`ob-${side}-row`}
        className="grid grid-cols-3 gap-2 items-center px-3 h-[22px] text-[11.5px] font-mono relative hover:bg-white/5 transition w-full">
        <div className="absolute right-0 top-0 bottom-0 pointer-events-none"
          style={{ width: `${w}%`, background: cBg }} />
        <span className="text-left relative z-10" style={{ color: c }}>
          {Number(lvl.price).toFixed(Math.min(8, Math.max(2, precision)))}
        </span>
        <span className="text-right relative z-10" style={{ color: T.text }}>{fmtQty(lvl.qty)}</span>
        <span className="text-right relative z-10" style={{ color: T.sub }}>{fmtQty(lvl.total)}</span>
      </button>
    );
  };

  const last = ticker?.last;

  return (
    <div className="flex flex-col h-full min-h-0" style={{ background: T.surface }} data-testid="trade-orderbook">
      {/* Header with title + view-mode icons + precision */}
      <div className="px-3 py-2 border-b" style={{ borderColor: T.border }}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-[13px] font-bold" style={{ color: T.blueLight }}>Order Book</h3>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex gap-1.5">
            {["split", "asks", "bids"].map((m) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`ob-mode-${m}`}
                className="p-1 rounded transition"
                style={{
                  background: mode === m ? T.surface3 : "transparent",
                  border: `1px solid ${mode === m ? T.blueLight : T.borderSoft}`,
                }}>
                <OrderBookIcon active={mode === m} mode={m} />
              </button>
            ))}
          </div>
          <select value={precision} onChange={(e) => setPrecision(Number(e.target.value))}
            data-testid="ob-precision"
            className="bg-transparent text-[11px] font-mono px-1.5 py-0.5 rounded outline-none"
            style={{ color: T.text, border: `1px solid ${T.borderSoft}` }}>
            {[8, 6, 4, 2].map((d) => (
              <option key={d} value={d}>{(0).toFixed(d).slice(0, -1) + "1"}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-wider"
        style={{ color: T.sub, borderBottom: `1px solid ${T.borderSoft}` }}>
        <span>Price ({quote})</span>
        <span className="text-right">Qty ({base})</span>
        <span className="text-right">Total</span>
      </div>
      {mode !== "bids" && (
        <div className="flex-1 min-h-0 overflow-y-auto trade-scroll flex flex-col justify-end">
          {askRows.length === 0 && (
            <div className="text-center py-4 text-[11px]" style={{ color: T.dim }}>No asks</div>
          )}
          {askRows.map((l, i) => <Row key={`a${i}-${l.price}`} lvl={l} side="ask" />)}
        </div>
      )}
      <div className="shrink-0 px-3 py-2 border-y flex items-center justify-between"
        style={{ borderColor: T.borderSoft, background: T.surface2 }}>
        <span className="text-[15px] font-bold font-mono" style={{ color: T.green }}
          data-testid="ob-mid-price">
          {last ? fmtPrice(last, quote) : (snap.best_bid ? fmtPrice(snap.best_bid, quote) : "—")}
        </span>
        {ticker?.change_pct_24h != null && (
          <span className="text-[11px] font-bold"
            style={{ color: ticker.change_pct_24h >= 0 ? T.green : T.red }}>
            {fmtPct(ticker.change_pct_24h)}
          </span>
        )}
      </div>
      {mode !== "asks" && (
        <div className="flex-1 min-h-0 overflow-y-auto trade-scroll">
          {bidRows.length === 0 && (
            <div className="text-center py-4 text-[11px]" style={{ color: T.dim }}>No bids</div>
          )}
          {bidRows.map((l, i) => <Row key={`b${i}-${l.price}`} lvl={l} side="bid" />)}
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Order form — Buy/Sell + Limit/Market + dual CTA + fee row
// --------------------------------------------------------------------------

// Stable, module-scope input row used by OrderForm. Defining this OUTSIDE
// OrderForm is critical — if it lived inside OrderForm's body, every
// re-render (which happens on every keystroke) would create a NEW component
// reference, causing React to unmount/remount the <input> and lose focus.
function Field({ label, value, onChange, suffix, testid, disabled }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider" style={{ color: T.dim }}>{label}</span>
      <div
        className="flex items-center h-[40px] rounded px-3"
        style={{ background: T.surface2, border: `1px solid ${T.borderSoft}` }}
      >
        <input
          type="text"
          inputMode="decimal"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ""))}
          data-testid={testid}
          className="bg-transparent outline-none flex-1 text-[14px] font-mono"
          style={{ color: T.text }}
        />
        <span className="text-[11px]" style={{ color: T.dim }}>{suffix}</span>
      </div>
    </div>
  );
}

function OrderForm({ pair, base, quote, balances, bestBid, bestAsk, prefillPrice, isAuthed, kycApproved, fees }) {
  const [type, setType] = useState("limit");
  const [side, setSide] = useState("buy");
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState("");
  const [quoteQty, setQuoteQty] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [marginHealth, setMarginHealth] = useState(null);

  useEffect(() => {
    if (!isAuthed) {
      setMarginHealth(null);
      return;
    }
    let cancelled = false;
    async function fetchHealth() {
      try {
        const { data } = await api.get("/api/safety/margin-health");
        if (!cancelled) setMarginHealth(data);
      } catch {}
    }
    fetchHealth();
    const interval = setInterval(fetchHealth, 15000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isAuthed, pair]);


  useEffect(() => { if (prefillPrice) setPrice(String(prefillPrice)); }, [prefillPrice]);

  useEffect(() => {
    if (type === "limit" && !price) {
      const best = side === "buy" ? bestAsk : bestBid;
      if (best) setPrice(String(best));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pair, type]);

  const baseBal = balances.find((b) => b.asset === base);
  const quoteBal = balances.find((b) => b.asset === quote);
  const tradable = (b) => Number(b?.trading_available ?? b?.available ?? 0);
  const availFor = side === "buy" ? tradable(quoteBal) : tradable(baseBal);
  const availAsset = side === "buy" ? quote : base;

  // Strip trailing zeroes from a decimal string (without scientific notation)
  const fmt8 = (n) => {
    if (!isFinite(n) || n <= 0) return "";
    const s = n.toFixed(8);
    return s.includes(".") ? s.replace(/\.?0+$/, "") : s;
  };

  // Reference price used for live preview when no explicit price (market mode)
  const refPrice = type === "limit"
    ? Number(price)
    : (side === "buy" ? Number(bestAsk) : Number(bestBid));

  // ---- Bidirectional handlers (Binance-style) ----
  // Editing Price keeps Amount fixed and recomputes Total.
  const onPriceChange = (v) => {
    setPrice(v);
    const p = Number(v); const q = Number(qty);
    if (p > 0 && q > 0) setQuoteQty(fmt8(p * q));
  };
  // Editing Amount recomputes Total using current ref price.
  const onQtyChange = (v) => {
    setQty(v);
    if (!v) { setQuoteQty(""); return; }
    const p = refPrice; const q = Number(v);
    if (p > 0 && q > 0) setQuoteQty(fmt8(p * q));
  };
  // Editing Total recomputes Amount using current ref price.
  const onQuoteQtyChange = (v) => {
    setQuoteQty(v);
    // Market BUY: Total IS the input — only update Amount preview.
    if (type === "market" && side === "buy") {
      if (!v) { setQty(""); return; }
      const p = Number(bestAsk); const t = Number(v);
      if (p > 0 && t > 0) setQty(fmt8(t / p));
      return;
    }
    if (!v) { setQty(""); return; }
    const p = refPrice; const t = Number(v);
    if (p > 0 && t > 0) setQty(fmt8(t / p));
  };

  function applyPct(pct) {
    const a = Number(availFor || 0);
    if (!a) return;
    if (type === "market" && side === "buy") {
      const t = a * pct;
      setQuoteQty(fmt8(t));
      const p = Number(bestAsk);
      if (p > 0) setQty(fmt8(t / p));
      return;
    }
    if (side === "buy") {
      // Limit BUY: spend (avail * pct) at current price
      const p = Number(price);
      if (!p) return;
      const total = a * pct;
      setQuoteQty(fmt8(total));
      setQty(fmt8(total / p));
    } else {
      // SELL: sell pct of base balance
      const q = a * pct;
      setQty(fmt8(q));
      const p = type === "limit" ? Number(price) : Number(bestBid);
      if (p > 0) setQuoteQty(fmt8(q * p));
    }
  }

  async function submit() {
    if (!isAuthed) {
      toast.error("Please log in to place orders.");
      return;
    }
    if (marginHealth?.trading_suspended) {
      toast.error("Trading is suspended for your account due to compliance restrictions.");
      return;
    }
    setSubmitting(true);
    try {
      const body = { pair, side, type };
      if (type === "limit") {
        if (!price || !qty) throw new Error("Enter price and amount.");
        body.price = price; body.qty = qty;
      } else if (side === "buy") {
        if (!quoteQty) throw new Error(`Enter total ${quote} to spend.`);
        body.quote_qty = quoteQty;
      } else {
        if (!qty) throw new Error(`Enter ${base} amount to sell.`);
        body.qty = qty;
      }
      const { data } = await api.post("/api/spot/orders", body);
      toast.success(`${side === "buy" ? "Buy" : "Sell"} order placed`, {
        description: `${data.order_id} • ${data.status}`,
      });
      setQty(""); setQuoteQty("");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitQuickBuy(quoteAmount) {
    if (!isAuthed) {
      toast.error("Please log in to place orders.");
      return;
    }
    if (!kycApproved) {
      toast.error("Trading locked — complete Trusted Access in Settings (KYC, wallet, IP, or device).");
      return;
    }
    const spend = Number(quoteAmount);
    if (!spend || spend <= 0) {
      toast.error(`Enter a valid ${quote} amount.`);
      return;
    }
    const q = balances.find((b) => b.asset === quote);
    const avail = Number(q?.trading_available ?? q?.available ?? 0);
    if (spend > avail) {
      toast.error(`Insufficient ${quote} balance.`);
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        pair,
        side: "buy",
        type: "market",
        quote_qty: String(spend),
      };
      const { data } = await api.post("/api/spot/orders", body);
      toast.success("Quick Buy order placed", {
        description: `${data.order_id} • ${data.status}`,
      });
      setType("market");
      setSide("buy");
      setQuoteQty(String(spend));
      onQuoteQtyChange(String(spend));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  const ctaColor = side === "buy" ? T.green : T.red;
  const ctaLabel = `${side === "buy" ? "Buy" : "Sell"} ${base}`;

  return (
    <div className="flex flex-col h-full min-h-0" style={{ background: T.surface }} data-testid="trade-order-form">
      {/* Buy/Sell pill toggle */}
      <div className="grid grid-cols-2 gap-1.5 p-3 pb-2">
        <button onClick={() => setSide("buy")} data-testid="of-side-buy"
          className="h-[36px] rounded font-bold text-[13px] uppercase tracking-wide transition"
          style={{
            background: side === "buy" ? T.green : T.surface2,
            color: side === "buy" ? "#fff" : T.sub,
            border: `1px solid ${side === "buy" ? T.green : T.borderSoft}`,
          }}>Buy</button>
        <button onClick={() => setSide("sell")} data-testid="of-side-sell"
          className="h-[36px] rounded font-bold text-[13px] uppercase tracking-wide transition"
          style={{
            background: side === "sell" ? T.red : T.surface2,
            color: side === "sell" ? "#fff" : T.sub,
            border: `1px solid ${side === "sell" ? T.red : T.borderSoft}`,
          }}>Sell</button>
      </div>

      {/* Limit/Market dropdown */}
      <div className="px-3">
        <select value={type} onChange={(e) => setType(e.target.value)}
          data-testid="of-type-select"
          className="w-full h-[36px] rounded px-3 text-[13px] font-bold bg-transparent outline-none"
          style={{ background: T.surface2, color: T.text, border: `1px solid ${T.borderSoft}` }}>
          <option value="limit">Limit</option>
          <option value="market">Market</option>
        </select>
      </div>

      <div className="px-3 py-3 space-y-2.5 flex-1 min-h-0 overflow-y-auto trade-scroll">
        {/* Available balance row */}
        <div className="flex items-center justify-between text-[11px]">
          <span style={{ color: T.dim }}>Available Balance:</span>
          <span className="font-mono" style={{ color: T.text }} data-testid="of-avail">
            {fmtQty(availFor || 0)} {availAsset}
          </span>
        </div>

        {type === "limit" && (
          <Field label="Price" value={price} onChange={onPriceChange} suffix={quote} testid="of-price" />
        )}

        {type === "market" && side === "buy" ? (
          <>
            <Field label="Total" value={quoteQty} onChange={onQuoteQtyChange} suffix={quote} testid="of-quote-qty" />
            <Field label="Amount" value={qty} onChange={() => {}} suffix={base} testid="of-qty" disabled />
          </>
        ) : (
          <Field label="Amount" value={qty} onChange={onQtyChange} suffix={base} testid="of-qty" />
        )}

        {type === "limit" && (
          <Field label="Total" value={quoteQty} onChange={onQuoteQtyChange} suffix={quote} testid="of-total" />
        )}

        {type === "market" && side === "sell" && (
          <Field label="Total" value={quoteQty} onChange={() => {}} suffix={quote} testid="of-total" disabled />
        )}

        <div className="grid grid-cols-4 gap-1.5 pt-1">
          {[0.25, 0.5, 0.75, 1.0].map((p) => (
            <button key={p} onClick={() => applyPct(p)} data-testid={`of-pct-${p * 100}`}
              className="h-[28px] rounded text-[11px] font-bold transition hover:opacity-80"
              style={{ background: T.surface2, color: T.sub, border: `1px solid ${T.borderSoft}` }}>
              {`${p * 100}%`}
            </button>
          ))}
        </div>

        {side === "buy" && (
          <div className="pt-2" data-testid="of-quick-buy">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: T.green }}>Quick Buy</span>
              <span className="text-[10px]" style={{ color: T.dim }}>— market, one tap</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(quote === "INR"
                ? [{ label: "₹500", amt: "500" }, { label: "₹2K", amt: "2000" }, { label: "₹5K", amt: "5000" }, { label: "₹10K", amt: "10000" }]
                : [{ label: "$10", amt: "10" }, { label: "$50", amt: "50" }, { label: "$100", amt: "100" }, { label: "$500", amt: "500" }]
              ).map(({ label, amt }) => (
                <button
                  key={amt}
                  type="button"
                  disabled={submitting}
                  data-testid={`of-quick-${amt}`}
                  onClick={() => submitQuickBuy(amt)}
                  className="h-[30px] px-3 rounded text-[11px] font-bold transition hover:opacity-90 disabled:opacity-50"
                  style={{ background: T.green + "22", color: T.green, border: `1px solid ${T.green}55` }}
                >
                  {label}
                </button>
              ))}
              <button
                type="button"
                disabled={submitting}
                data-testid="of-quick-max"
                onClick={() => {
                  const max = fmt8(Number(availFor || 0));
                  submitQuickBuy(max);
                }}
                className="h-[30px] px-3 rounded text-[11px] font-bold disabled:opacity-50"
                style={{ background: T.surface2, color: T.text, border: `1px solid ${T.borderSoft}` }}
              >
                Max
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Portfolio Health Indicator */}
      {isAuthed && marginHealth && (
        <div className="px-3 pb-3 pt-1 text-[11px] space-y-1" style={{ borderTop: `1px solid ${T.borderSoft}` }}>
          <div className="flex justify-between items-center text-[#8892A8]">
            <span>Portfolio Health:</span>
            <span className="font-bold" style={{ 
              color: marginHealth.margin_health_score >= 70 ? T.green : marginHealth.margin_health_score >= 30 ? "#F59E0B" : T.red 
            }}>
              {marginHealth.margin_health_score}% ({marginHealth.status})
            </span>
          </div>
          <div className="w-full bg-[#0B1018] rounded-full h-1.5 overflow-hidden border border-[#1A2230]">
            <div className="h-full rounded-full transition-all duration-500" style={{
              width: `${marginHealth.margin_health_score}%`,
              backgroundColor: marginHealth.margin_health_score >= 70 ? T.green : marginHealth.margin_health_score >= 30 ? "#F59E0B" : T.red
            }} />
          </div>
          {marginHealth.largest_single_asset_pct > 50 && (
            <div className="text-[10px] text-[#F59E0B] flex items-center gap-1 mt-1">
              <span>⚠️ High concentration: {marginHealth.largest_single_asset_pct}% in {marginHealth.largest_asset}</span>
            </div>
          )}
          {marginHealth.trading_suspended && (
            <div className="text-[10px] text-[#EF4444] font-semibold mt-1">
              ❌ Trading Suspended: compliance freeze active
            </div>
          )}
        </div>
      )}

      {/* CTA: dual when logged out, KYC nudge when verified-not-approved, single when fully approved */}

      <div className="px-3 pb-2 space-y-2">
        {isAuthed ? (
          kycApproved ? (
            <button onClick={submit} disabled={submitting || marginHealth?.trading_suspended} data-testid="of-submit"
              className="w-full h-[44px] rounded font-bold text-[14px] uppercase tracking-wide transition disabled:opacity-60"
              style={{ background: ctaColor, color: "#fff" }}>
              {submitting ? <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Placing…</span> : ctaLabel}
            </button>
          ) : (
            <Link to="/settings" data-testid="of-kyc-cta"
              className="w-full h-[44px] rounded font-bold text-[14px] uppercase tracking-wide transition hover:opacity-90 inline-flex items-center justify-center gap-2"
              style={{ background: T.blue, color: "#fff" }}>
              Unlock Trading (Trusted Access)
            </Link>
          )
        ) : (
          <>
            <Link to="/register" data-testid="of-signup-link"
              className="block w-full h-[42px] rounded font-bold text-[14px] uppercase tracking-wide transition hover:opacity-90 inline-flex items-center justify-center"
              style={{ background: T.blue, color: "#fff" }}>
              Sign Up
            </Link>
            <Link to="/login" data-testid="of-login-link"
              className="block w-full h-[42px] rounded font-bold text-[14px] uppercase tracking-wide transition inline-flex items-center justify-center"
              style={{ background: T.surface2, color: T.text, border: `1px solid ${T.borderSoft}` }}>
              Log In
            </Link>
          </>
        )}
      </div>

      {/* Fee row */}
      <div className="px-3 pb-3 pt-1 flex items-center justify-between text-[11px]"
        style={{ borderTop: `1px solid ${T.borderSoft}` }} data-testid="of-fee-row">
        <span style={{ color: T.dim }}>
          Fee: <span style={{ color: T.text }}>Maker: {fees?.maker || "0.1"}%</span>
          {" | "}
          <span style={{ color: T.text }}>Taker: {fees?.taker || "0.1"}%</span>
        </span>
        <span className="px-1.5 py-0.5 rounded text-[9.5px] font-bold tracking-wider"
          style={{ background: T.red, color: "#fff" }}>HOT</span>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Market Traders side panel (live trade tape)
// --------------------------------------------------------------------------
function MarketTraders({ trades, base, quote }) {
  return (
    <div className="flex flex-col h-full min-h-0" style={{ background: T.surface }} data-testid="trade-market-traders">
      <div className="px-3 py-2 border-b" style={{ borderColor: T.border }}>
        <h3 className="text-[13px] font-bold" style={{ color: T.blueLight }}>Market Traders</h3>
      </div>
      <div className="grid grid-cols-3 gap-2 px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-wider"
        style={{ color: T.sub, borderBottom: `1px solid ${T.borderSoft}` }}>
        <span>Price ({quote})</span>
        <span className="text-right">Qty ({base})</span>
        <span className="text-right">Time</span>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto trade-scroll">
        {trades.length === 0 && (
          <div className="text-center py-4 text-[11px]" style={{ color: T.dim }}>Loading trades…</div>
        )}
        {trades.slice(0, 80).map((t) => (
          <div key={t.trade_id} className="grid grid-cols-3 gap-2 px-3 h-[22px] items-center text-[11px] font-mono"
            data-testid="mt-row">
            <span style={{ color: t.side === "buy" ? T.green : T.red }}>{fmtPrice(t.price, quote)}</span>
            <span className="text-right" style={{ color: T.text }}>{fmtQty(t.qty)}</span>
            <span className="text-right" style={{ color: T.dim }}>{timeOnly(t.created_at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Open / history / trades panel
// --------------------------------------------------------------------------
function OrdersPanel({ openOrders, onCancel, isAuthed, history, myTrades, quote }) {
  const [tab, setTab] = useState("open");
  const Tab = ({ k, label, count }) => (
    <button onClick={() => setTab(k)} data-testid={`op-tab-${k}`}
      className="relative px-4 h-[40px] text-[12.5px] font-bold transition"
      style={{ color: tab === k ? T.blueLight : T.sub }}>
      {label}{count != null && <span className="ml-1.5 text-[10px]" style={{ color: T.dim }}>({count})</span>}
      {tab === k && <span className="absolute left-3 right-3 bottom-0 h-[2px]" style={{ background: T.blueLight }} />}
    </button>
  );
  return (
    <div className="flex flex-col h-full min-h-0" style={{ background: T.surface }} data-testid="trade-orders-panel">
      <div className="flex border-b" style={{ borderColor: T.border }}>
        <Tab k="open" label="Open Orders" count={openOrders.length} />
        <Tab k="history" label="Order History" count={history.length} />
        <Tab k="trades" label="Trade History" count={myTrades.length} />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto trade-scroll">
        {!isAuthed ? (
          <div className="text-center py-12 text-[12px]" style={{ color: T.dim }}>
            <Link to="/login" className="underline" style={{ color: T.blueLight }} data-testid="op-login-link">
              Log in
            </Link>{" "}to see your orders.
          </div>
        ) : tab === "open" ? (
          <OrdersTable rows={openOrders} cols={["Date", "Trading Pair", "Type", "Side", "Price", "Amount", "Filled", "Status", ""]}
            render={(o) => [
              timeOnly(o.created_at),
              o.pair,
              o.type,
              <Side key="s" side={o.side} />,
              o.type === "market" ? "—" : fmtPrice(o.price, quote),
              fmtQty(o.qty || o.quote_qty),
              `${(((+o.filled || 0) / (+o.qty || 1)) * 100).toFixed(1)}%`,
              <span key="st" style={{ color: T.sub }}>{o.status}</span>,
              <button key="x" onClick={() => onCancel(o.order_id)} data-testid={`op-cancel-${o.order_id}`}
                className="px-2 py-1 rounded text-[11px] font-bold hover:bg-white/5 transition"
                style={{ color: T.red, border: `1px solid ${T.red}` }}>Cancel</button>,
            ]} emptyMsg="No open orders." />
        ) : tab === "history" ? (
          <OrdersTable rows={history} cols={["Date", "Trading Pair", "Type", "Side", "Price", "Amount", "Filled", "Status"]}
            render={(o) => {
              // For market orders, the entered price is null — show the
              // average fill price instead so users see what they actually paid.
              const filled = +o.filled || 0;
              const filledQuote = +o.filled_quote || 0;
              const avgPrice = filled > 0 ? filledQuote / filled : 0;
              const priceCell =
                o.type === "market"
                  ? (avgPrice > 0 ? fmtPrice(avgPrice, quote) : "—")
                  : fmtPrice(o.price, quote);
              return [
                timeOnly(o.updated_at || o.created_at),
                o.pair, o.type, <Side key="s" side={o.side} />,
                priceCell,
                fmtQty(o.qty || o.quote_qty), fmtQty(o.filled),
                <span key="st" style={{ color: o.status === "filled" ? T.green : T.dim }}>{o.status}</span>,
              ];
            }} emptyMsg="No order history yet." />
        ) : (
          <OrdersTable rows={myTrades} cols={["Date", "Trading Pair", "Side", "Role", "Price", "Amount", "Value", "Fee"]}
            render={(t) => [
              timeOnly(t.created_at),
              t.pair, <Side key="s" side={t.side} />,
              <span key="r" style={{ color: T.sub }}>{t.role}</span>,
              fmtPrice(t.price, quote), fmtQty(t.qty), fmtQty(t.value),
              `${fmtQty(t.role === "maker" ? t.maker_fee : t.taker_fee)} ${t.role === "maker" ? t.maker_fee_asset : t.taker_fee_asset}`,
            ]} emptyMsg="No trades yet." />
        )}
      </div>
    </div>
  );
}

function Side({ side }) {
  return <span style={{ color: side === "buy" ? T.green : T.red }}
    className="font-bold text-[11px] uppercase">{side}</span>;
}

function OrdersTable({ rows, cols, render, emptyMsg }) {
  return (
    <table className="w-full">
      <thead>
        <tr>
          {cols.map((c, i) => (
            <th key={i} className="px-3 py-2 text-left text-[10.5px] font-bold uppercase tracking-wider"
              style={{ color: T.sub, borderBottom: `1px solid ${T.borderSoft}`, background: T.surface2 }}>
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={cols.length} className="py-12 text-center text-[12px]" style={{ color: T.dim }}>
            {emptyMsg}
          </td></tr>
        ) : rows.map((r, i) => (
          <tr key={r.order_id || r.trade_id || i} data-testid="op-row"
            style={{ borderBottom: `1px solid ${T.borderSoft}` }}>
            {render(r).map((c, j) => (
              <td key={j} className="px-3 py-2 text-[12px] font-mono" style={{ color: T.text }}>{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// --------------------------------------------------------------------------
// Main Trade page
// --------------------------------------------------------------------------
export default function Trade() {
  const { pair: pairParam } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAuthed = !!user && user !== false;
  const [canTrade, setCanTrade] = useState(false);

  useEffect(() => {
    if (!isAuthed) {
      setCanTrade(false);
      return;
    }
    api.get("/api/trust/status")
      .then(({ data }) => setCanTrade(!!data?.can_trade))
      .catch(() => setCanTrade(user?.kyc_status === "approved"));
  }, [isAuthed, user?.kyc_status, user?.id]);

  const [base, quote] = useMemo(() => {
    const p = (pairParam || "BTC-USDT").toUpperCase();
    const [b, q] = p.split("-");
    return [b || "BTC", q || "USDT"];
  }, [pairParam]);
  const pair = `${base}/${quote}`;

  // Pair config (fees)
  const [pairCfg, setPairCfg] = useState(null);
  useEffect(() => {
    api.get("/api/spot/pairs").then(({ data }) => {
      const found = (data || []).find((p) => p.pair === pair);
      setPairCfg(found || null);
    }).catch(() => {});
  }, [pair]);

  // ---------- WebSockets ----------
  const wsBase = useMemo(() => wsBaseUrl(), []);

  // Ticker — one WS channel (snapshot + live), not per-pair HTTP polling
  const [ticker, setTicker] = useState(null);
  useEffect(() => { setTicker(null); }, [base, quote]);
  useWSChannel(`${wsBase}/api/ws/ticker/${base}/${quote}`, (msg) => {
    if (msg.type === "ticker" || msg.last != null) setTicker(msg);
  }, [base, quote]);

  const [ob, setOb] = useState({ bids: [], asks: [] });

  // REST snapshot on pair change; WS keeps book live (do not clear — avoids blank flash).
  useEffect(() => {
    let cancelled = false;
    api
      .get(`/api/spot/orderbook/${base}/${quote}`, { params: { depth: 25 } })
      .then(({ data }) => {
        if (!cancelled && data && ((data.bids?.length) || (data.asks?.length))) {
          setOb(data);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [base, quote]);

  useWSChannel(`${wsBase}/api/ws/orderbook/${base}/${quote}`, (msg) => {
    if (msg.type === "orderbook_snapshot") setOb(msg);
  }, [base, quote]);

  const [trades, setTrades] = useState([]);
  useWSChannel(`${wsBase}/api/ws/trades/${base}/${quote}`, (msg) => {
    if (msg.type === "trades_snapshot") setTrades(msg.trades.slice().reverse());
    else if (msg.type === "trade") setTrades((prev) => [msg, ...prev].slice(0, 200));
  }, [base, quote]);

  const [interval, setInterval2] = useState("15m");

  // ---------- User WS + REST hydration ----------
  const [balances, setBalances] = useState([]);
  const [openOrders, setOpenOrders] = useState([]);
  const [history, setHistory] = useState([]);
  const [myTrades, setMyTrades] = useState([]);

  useEffect(() => {
    if (!isAuthed) {
      setBalances([]); setOpenOrders([]); setHistory([]); setMyTrades([]);
      return;
    }
    api.get("/api/spot/balances").then(({ data }) => setBalances(data.balances || [])).catch(() => {});
    api.get("/api/spot/orders/open").then(({ data }) => setOpenOrders(data || [])).catch(() => {});
    api.get(`/api/spot/orders/history?pair=${pair}&limit=50`).then(({ data }) => setHistory(data || [])).catch(() => {});
    api.get(`/api/spot/my-trades?pair=${pair}&limit=50`).then(({ data }) => setMyTrades(data || [])).catch(() => {});
  }, [isAuthed, pair]);

  useWSChannel(isAuthed ? `${wsBase}/api/ws/user` : null, (msg) => {
    if (msg.type === "balance_update") setBalances(msg.balances || []);
    else if (msg.type === "open_orders_snapshot") setOpenOrders(msg.orders || []);
    else if (msg.type === "order_update") {
      const o = msg.order; if (!o) return;
      const isOpen = ["open", "partial"].includes(o.status);
      setOpenOrders((prev) => {
        const others = prev.filter((x) => x.order_id !== o.order_id);
        return isOpen ? [o, ...others] : others;
      });
      if (!isOpen) setHistory((prev) => [o, ...prev.filter((x) => x.order_id !== o.order_id)].slice(0, 100));
    } else if (msg.type === "user_trade") {
      const t = { ...msg.trade, role: msg.role };
      setMyTrades((prev) => [t, ...prev].slice(0, 100));
    }
  }, [isAuthed]);

  const [prefillPrice, setPrefillPrice] = useState(null);
  const handlePickPrice = useCallback((p) => setPrefillPrice({ value: p, ts: Date.now() }), []);

  async function cancelOrder(orderId) {
    try {
      await api.delete(`/api/spot/orders/${orderId}`);
      toast.success("Order cancelled");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  return (
    <div
      className="h-screen overflow-hidden flex flex-col"
      style={{ background: T.bg, color: T.text, fontFamily: "Inter, system-ui, sans-serif" }}
      data-testid="trade-page"
    >
      <Toaster theme="dark" position="top-right" richColors />
      <div className="shrink-0 z-40 bg-white border-b border-[var(--cm-border)]">
        <Header />
      </div>
      <div className="shrink-0">
        <InfoBar pair={pair} base={base} quote={quote} ticker={ticker}
          onPickPair={(p) => nav(`/trade/spot/${p.base}-${p.quote}`)} />
      </div>

      {/* Workspace — fixed viewport; panels scroll internally */}
      <div
        className="flex-1 min-h-0 grid gap-[1px] p-[1px]"
        style={{
          background: T.border,
          gridTemplateColumns: "240px 1fr 300px 300px",
          gridTemplateRows: "1fr 200px",
          gridTemplateAreas: `
            "pairs chart book trade"
            "pairs orders book trade"
          `,
        }}
      >
        <div className="min-h-0 overflow-hidden" style={{ gridArea: "pairs" }}>
          <PairListSidebar
            currentPair={pair}
            onSelect={(p) => nav(`/trade/spot/${p.base}-${p.quote}`)}
          />
        </div>
        <div className="min-h-0 overflow-hidden" style={{ gridArea: "chart" }}>
          <ChartWorkspace base={base} quote={quote} pair={pair}
            interval={interval} setInterval={setInterval2}
            ticker={ticker} orderBook={ob} />
        </div>
        <div
          className="min-h-0 overflow-hidden flex flex-col"
          style={{ gridArea: "book", gridRow: "1 / span 2" }}
        >
          <div className="flex-[3] min-h-0 overflow-hidden">
            <OrderBook snap={ob} base={base} quote={quote}
              onPickPrice={handlePickPrice} ticker={ticker} />
          </div>
          <div className="flex-[2] min-h-0 overflow-hidden border-t" style={{ borderColor: T.border }}>
            <MarketTraders trades={trades} base={base} quote={quote} />
          </div>
        </div>
        <div
          className="min-h-0 overflow-hidden"
          style={{ gridArea: "trade", gridRow: "1 / span 2" }}
        >
          <OrderForm pair={pair} base={base} quote={quote}
            balances={balances}
            bestBid={ob.best_bid} bestAsk={ob.best_ask}
            prefillPrice={prefillPrice?.value}
            isAuthed={isAuthed}
            kycApproved={canTrade}
            fees={{ maker: pairCfg?.maker_fee_pct, taker: pairCfg?.taker_fee_pct }} />
        </div>
        <div className="min-h-0 overflow-hidden" style={{ gridArea: "orders" }}>
          <OrdersPanel openOrders={openOrders} history={history} myTrades={myTrades}
            isAuthed={isAuthed} quote={quote} onCancel={cancelOrder} />
        </div>
      </div>
    </div>
  );
}
